// idle-scheduler — cede a main thread entre pedaços de trabalho pesado.
//
// Uso: processar listas grandes (agrupamento/ordenação de ~1.041 canais) em
// CHUNKS, chamando `await yieldToMain()` entre eles. Assim o browser pinta o
// frame e processa input do D-pad — a tela NÃO congela por segundos, ela enche
// progressivamente.
//
// Por que MessageChannel e não setTimeout(0): depois de aninhar timers, o
// browser aplica clamp de 4ms por tick (HTML spec) — num loop de N chunks isso
// vira dezenas de ms de atraso puro. MessageChannel posta um macrotask SEM
// clamp: cede o frame e retoma no próximo tick, sem o pedágio dos 4ms.
//
// Compat: MessageChannel existe no Chr38+ (piso da TV; já usado em cleanup.ts
// pra falar com o Service Worker). requestIdleCallback NÃO é confiável no piso
// (Chr47+ e ausente em webOS 3.5/Chr38), então não dependemos dele. Fallback
// pra setTimeout se MessageChannel faltar (ambiente de teste/SSR estranho).

/** Cede a main thread e resolve no próximo macrotask (sem clamp de 4ms). */
export function yieldToMain(): Promise<void> {
  return new Promise<void>((resolve) => {
    if (typeof MessageChannel === 'function') {
      const channel = new MessageChannel()
      channel.port1.onmessage = () => {
        channel.port1.close()
        resolve()
      }
      channel.port2.postMessage(undefined)
      return
    }
    // Fallback: setTimeout(0). Só cai aqui em ambiente sem MessageChannel.
    setTimeout(resolve, 0)
  })
}

/**
 * Orçamento de tempo por fatia: quando uma fatia de trabalho passa disso, é hora
 * de ceder. ~8ms deixa folga dentro de um frame de ~16ms (60fps) pra o browser
 * pintar e tratar input. Em device low/vlow o frame é maior, mas 8ms mantém a
 * responsividade do D-pad boa (foco < 100ms de orçamento, ver ux-tv.md).
 */
export const FRAME_BUDGET_MS = 8
